import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const FEATURES = [
  { title: 'Live nurse tracking', desc: 'See your assigned nurse on the map — app only.' },
  { title: 'Digital certificates', desc: 'Vaccination certificates stored in-app.' },
  { title: 'Family vaccine timeline', desc: 'One timeline for every family member.' },
  { title: 'One-tap rebooking', desc: 'Rebook the same vaccine from your last visit.' },
];

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

export default function AppPreview() {
  const isMobile = useIsMobile();

  return (
    <section
      data-testid="app-preview-section"
      style={{
        padding: isMobile ? '40px 16px 60px' : '80px 20px 120px 20px',
        position: 'relative',
        fontFamily: 'Montserrat, sans-serif',
        background: '#fdfcff',
        overflowX: 'hidden',
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.65 }}
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          background: 'linear-gradient(135deg, #3e195e 0%, #673c96 60%, #f37721 140%)',
          borderRadius: isMobile ? 20 : 24,
          position: 'relative',
          overflow: isMobile ? 'hidden' : 'visible',
          boxShadow: '0 20px 60px rgba(103,60,150,0.30)',
        }}
      >
        {/* Grain overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cdefs%3E%3Cpattern id='grain' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='25' cy='25' r='1' fill='white' opacity='0.06'/%3E%3Ccircle cx='75' cy='75' r='1' fill='white' opacity='0.06'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grain)'/%3E%3C/svg%3E\")",
            opacity: 0.4,
            borderRadius: isMobile ? 20 : 24,
            pointerEvents: 'none',
          }}
        />

        {/* ─── INNER WRAPPER ─── */}
        <div
          className="app-preview-inner"
          style={{
            padding: isMobile ? '36px 24px 40px' : '44px 60px',
            position: 'relative',
            zIndex: 2,
          }}
        >

          {/* ─── LEFT: Text content ─── */}
          <div
            className="app-preview-content"
            style={{ maxWidth: isMobile ? '100%' : 'calc(100% - 310px)' }}
          >
            {/* Badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,0.18)',
                color: 'white',
                padding: '6px 16px',
                borderRadius: 50,
                fontSize: '0.72rem',
                fontWeight: 700,
                marginBottom: 16,
                border: '1px solid rgba(255,255,255,0.28)',
                backdropFilter: 'blur(10px)',
                textTransform: 'uppercase',
              }}
            >
              <span>✦</span> App experience · Coming soon
            </div>

            {/* Heading */}
            <h2
              style={{
                fontSize: isMobile ? '1.55rem' : 'clamp(1.6rem, 3vw, 2.2rem)',
                fontWeight: 900,
                lineHeight: 1.15,
                color: 'white',
                marginBottom: 12,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              The BookMyJab app is coming soon
            </h2>

            {/* Subtext */}
            <p
              style={{
                fontSize: '0.9rem',
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.85)',
                marginBottom: 24,
                maxWidth: isMobile ? '100%' : 480,
              }}
            >
              Book on the website today. These extras will live only in the iOS and Android app when it launches.
            </p>

            {/* Features grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: isMobile ? '12px' : '16px 20px',
                marginBottom: '28px',
              }}
            >
              {FEATURES.map((f, idx) => (
                <div key={idx}>
                  <h4 style={{
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    margin: '0 0 2px',
                    fontFamily: 'Poppins, sans-serif',
                    color: '#fff',
                  }}>
                    • {f.title}
                  </h4>
                  <p style={{
                    fontSize: '0.73rem',
                    lineHeight: 1.4,
                    color: 'rgba(255,255,255,0.7)',
                    margin: 0,
                    paddingLeft: 10,
                  }}>
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <a
                href="/vaccination-catalogue"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '11px 24px',
                  borderRadius: 12,
                  textDecoration: 'none',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  background: 'white',
                  color: '#3e195e',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Book now
              </a>
              <span style={{
                fontSize: '0.72rem',
                color: 'rgba(255,255,255,0.6)',
                fontWeight: 500,
                maxWidth: 160,
              }}>
                App Store and Google Play listings coming soon.
              </span>
            </div>
          </div>

          {/* ─── PHONE MOCKUP ─── */}
          {isMobile ? (
            /* ── MOBILE: centered below content, tilted ── */
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 36,
              marginBottom: 20,
              paddingBottom: 20,
            }}>
              <motion.div
                initial={{ opacity: 0, y: 24, rotate: 10 }}
                whileInView={{ opacity: 1, y: 0, rotate: 10 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                style={{
                  width: 200,
                  height: 420,
                  background: '#0a0a12',
                  borderRadius: 34,
                  boxShadow: '0 0 0 2px #2a2a3e, 0 0 0 7px #0a0a12, 0 20px 50px rgba(0,0,0,0.5)',
                  padding: 7,
                  position: 'relative',
                }}
              >
                {/* Side buttons */}
                <div style={{ position: 'absolute', right: -3, top: 80, width: 3, height: 40, background: '#1e1e30', borderRadius: 2 }} />
                <div style={{ position: 'absolute', left: -3, top: 70, width: 3, height: 28, background: '#1e1e30', borderRadius: 2 }} />
                <div style={{ position: 'absolute', left: -3, top: 106, width: 3, height: 28, background: '#1e1e30', borderRadius: 2 }} />

                {/* Screen */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 27,
                  overflow: 'hidden',
                  background: '#ffffff',
                }}>
                  <img
                    src="/phone-ss.png"
                    alt="BookMyJab App preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'top center',
                      display: 'block',
                    }}
                  />
                </div>
              </motion.div>
            </div>
          ) : (
            /* ── DESKTOP: absolute, tilted, pops above card ── */
            <div
              className="app-preview-phone-section"
              style={{
                position: 'absolute',
                right: 0,
                top: -60,
                bottom: 0,
                width: 300,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: 20,
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20, rotate: 12 }}
                whileInView={{ opacity: 1, y: 0, rotate: 12 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2 }}
                whileHover={{ scale: 1.02 }}
                style={{
                  width: 230,
                  height: 480,
                  background: '#0a0a12',
                  borderRadius: 38,
                  boxShadow: '0 0 0 2px #2a2a3e, 0 0 0 8px #0a0a12, 0 24px 60px rgba(0,0,0,0.55)',
                  padding: 8,
                  position: 'relative',
                }}
              >
                {/* Side buttons */}
                <div style={{ position: 'absolute', right: -3, top: 90, width: 3, height: 48, background: '#1e1e30', borderRadius: 2 }} />
                <div style={{ position: 'absolute', left: -3, top: 80, width: 3, height: 32, background: '#1e1e30', borderRadius: 2 }} />
                <div style={{ position: 'absolute', left: -3, top: 120, width: 3, height: 32, background: '#1e1e30', borderRadius: 2 }} />
                <div style={{ position: 'absolute', left: -3, top: 160, width: 3, height: 32, background: '#1e1e30', borderRadius: 2 }} />

                {/* Screen */}
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 30,
                  overflow: 'hidden',
                  background: '#ffffff',
                }}>
                  <img
                    src="/phone-ss.png"
                    alt="BookMyJab App preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'top center',
                      display: 'block',
                    }}
                  />
                </div>
              </motion.div>
            </div>
          )}

        </div>
      </motion.div>
    </section>
  );
}
